import { useEffect } from 'react';
import axios from 'axios';

/**
 * Demand capture: tell the backend what people looked for and could not find.
 *
 * Two signals, both sent to POST /marketplace/v3/demand:
 *   - search_no_results: a marketplace search that came back empty, sent once
 *     per distinct query after the empty state has stood for 1.5s, so a query
 *     typed letter by letter is not sent as "cru", "crun", "crunc"...
 *   - brand_request: the person told us which brand they wanted.
 *
 * The web client is the only source of search_no_results; the backend search
 * endpoint does not log (see the backend PR). Every call fails silently: the
 * endpoint may not be deployed yet, and demand capture must never break search.
 */

const ENDPOINT = '/marketplace/v3/demand';
const ANON_KEY = 'fleek_demand_aid';

export const NO_RESULTS_DELAY_MS = 1500;
export const MIN_SEARCH_LENGTH = 3;
export const MIN_REQUEST_LENGTH = 2;
const MAX_LENGTH = 120;

// Per page load: one signal per kind per distinct query.
const sent = new Set();

export const normaliseQuery = (query) =>
  String(query ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

const randomId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

// A random id kept in this browser so distinct signed-out people can be
// counted. Not the analytics id, and nothing that identifies anyone.
const anonymousId = () => {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch (err) {
    return undefined;
  }
};

/**
 * Send one signal. Resolves to true when it was sent and accepted, false
 * otherwise (too short, already sent, or the request failed). Never rejects.
 */
export const reportDemand = (kind, query) => {
  const normalised = normaliseQuery(query);
  const min = kind === 'brand_request' ? MIN_REQUEST_LENGTH : MIN_SEARCH_LENGTH;
  const key = `${kind}:${normalised}`;
  if (normalised.length < min || sent.has(key)) return Promise.resolve(false);
  sent.add(key);

  try {
    return axios
      .post(ENDPOINT, {
        kind,
        query: String(query).trim().slice(0, MAX_LENGTH),
        platform: 'web',
        anonymous_id: anonymousId(),
      })
      // The app-wide interceptor resolves failed requests with the error.
      .then((res) => res?.status === 204)
      .catch(() => false);
  } catch (err) {
    return Promise.resolve(false);
  }
};

/**
 * Report a search with no results once the empty state has stood for
 * NO_RESULTS_DELAY_MS. A new query, or results arriving, cancels the wait.
 */
export const useNoResultsCapture = (query, noResults) => {
  useEffect(() => {
    if (!noResults || normaliseQuery(query).length < MIN_SEARCH_LENGTH) return undefined;
    const timer = setTimeout(() => reportDemand('search_no_results', query), NO_RESULTS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query, noResults]);
};

// Tests only.
export const resetDemandCapture = () => sent.clear();
